import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../../components/AdminLayout";
import QRScannerModal from "../../components/QRScannerModal";
import api, { formatApiError } from "../../lib/api";
import { Plus, Pencil, Trash2, QrCode, X, Download, Upload, ScanLine } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
    name: "",
    roll_number: "",
    department: "",
    phone: "",
    email: "",
    assigned_bus_id: "",
    payment_status: "due",
    qr_code: "",
};

export default function StudentsPage() {
    const navigate = useNavigate();
    const [students, setStudents] = useState([]);
    const [buses, setBuses] = useState([]);
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [qrOpen, setQrOpen] = useState(null); // student object
    const [scannerOpen, setScannerOpen] = useState(false);

    const load = async () => {
        const [s, b] = await Promise.all([api.get("/students"), api.get("/buses")]);
        setStudents(s.data);
        setBuses(b.data);
    };
    useEffect(() => {
        load();
    }, []);

    const openCreate = () => {
        setEditing(null);
        setForm({ ...EMPTY, assigned_bus_id: buses[0]?.id || "" });
        setFormOpen(true);
    };
    const openEdit = (s) => {
        setEditing(s);
        setForm({
            name: s.name,
            roll_number: s.roll_number,
            department: s.department,
            phone: s.phone || "",
            email: s.email || "",
            assigned_bus_id: s.assigned_bus_id || "",
            payment_status: s.payment_status,
            qr_code: s.qr_token || "",
        });
        setFormOpen(true);
    };

    const save = async (e) => {
        e.preventDefault();
        const { qr_code, ...studentFields } = form;
        try {
            const payload = { ...studentFields, assigned_bus_id: form.assigned_bus_id || null };
            let studentId = editing?.id;
            if (editing) {
                await api.patch(`/students/${editing.id}`, payload);
            } else {
                const { data } = await api.post("/students", payload);
                studentId = data.id;
            }

            const previousCode = editing?.qr_token || "";
            if (qr_code !== previousCode) {
                if (qr_code) {
                    await api.post(`/students/${studentId}/assign-qr`, { qr_code });
                } else if (previousCode) {
                    await api.post(`/students/${studentId}/unassign-qr`);
                }
            }

            toast.success(editing ? "Student updated" : "Student added");
            setFormOpen(false);
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
        }
    };

    // Cards only carry the QR artwork — there's no code printed for a human to
    // read — so allotment happens by scanning, not by picking from a list.
    // When editing an existing student we assign against the backend immediately
    // (assign-qr already rejects unknown/lost/already-assigned codes with a clear
    // message). For a brand-new student (no id yet) we can't call assign-qr until
    // the student is saved, so we look the scanned code up live first — checking
    // it's actually in the pool and still "available" (not assigned/lost) — before
    // accepting it locally; the real assignment then happens after save() creates
    // the student.
    const handleScanned = async (code) => {
        setScannerOpen(false);
        const normalized = code.trim().toUpperCase();
        if (!normalized) return;

        if (editing) {
            try {
                await api.post(`/students/${editing.id}/assign-qr`, { qr_code: normalized });
                toast.success(`Code ${normalized} assigned`);
                setForm((f) => ({ ...f, qr_code: normalized }));
                setEditing((e) => ({ ...e, qr_token: normalized }));
                await load();
            } catch (err) {
                toast.error(formatApiError(err.response?.data?.detail) || "Could not assign this code");
            }
            return;
        }

        try {
            const { data } = await api.get("/qr-pool", { params: { search: normalized } });
            const entry = data.find((c) => c.code.toUpperCase() === normalized);
            if (!entry) {
                toast.error("This code isn't in the QR pool. Import it first.");
                return;
            }
            if (entry.status === "lost") {
                toast.error("This card was reported lost and can't be allotted.");
                return;
            }
            if (entry.status === "assigned") {
                toast.error("This code is already assigned to another student.");
                return;
            }
            setForm((f) => ({ ...f, qr_code: entry.code }));
            toast.success(`Code ${entry.code} scanned`);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not verify this code");
        }
    };

    const clearQr = async () => {
        if (editing?.qr_token) {
            try {
                await api.post(`/students/${editing.id}/unassign-qr`);
                toast.success("QR code unassigned");
                setEditing((e) => ({ ...e, qr_token: null }));
                await load();
            } catch (err) {
                toast.error(formatApiError(err.response?.data?.detail) || "Could not unassign");
                return;
            }
        }
        setForm((f) => ({ ...f, qr_code: "" }));
    };

    const reportLost = async () => {
        if (!editing?.qr_token) return;
        if (!window.confirm(`Report ${editing.qr_token} as lost? It can never be allotted again.`)) return;
        try {
            await api.post(`/students/${editing.id}/report-lost-qr`);
            toast.success("Card reported lost — allot a new one below");
            setEditing({ ...editing, qr_token: null });
            setForm({ ...form, qr_code: "" });
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not report lost");
        }
    };

    const togglePayment = async (s) => {
        const next = s.payment_status === "paid" ? "due" : "paid";
        await api.patch(`/students/${s.id}`, { payment_status: next });
        toast.success(`Marked as ${next.toUpperCase()}`);
        load();
    };

    const remove = async (s) => {
        if (!window.confirm(`Delete ${s.name}?`)) return;
        await api.delete(`/students/${s.id}`);
        toast.success("Student deleted");
        load();
    };

    const busById = (id) => buses.find((b) => b.id === id);

    const filtered = students.filter((s) => {
        if (filter !== "all" && s.payment_status !== filter) return false;
        if (search) {
            const q = search.toLowerCase();
            return (
                s.name.toLowerCase().includes(q) ||
                s.roll_number.toLowerCase().includes(q) ||
                s.department.toLowerCase().includes(q)
            );
        }
        return true;
    });

    return (
        <AdminLayout>
            <div className="p-4 sm:p-8 lg:p-12 max-w-[1400px]">
                <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
                    <div>
                        <p className="label-caps mb-2">Management</p>
                        <h1 className="font-display text-5xl text-[#111111]">Students</h1>
                        <p className="text-[#666666] mt-2">
                            {students.length} total · {students.filter((s) => s.payment_status === "paid").length} paid ·{" "}
                            {students.filter((s) => s.payment_status === "due").length} due
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={() => navigate("/admin/students/import")}
                            data-testid="import-students-button"
                            className="bg-white text-[#111111] border border-[#111111] font-bold px-5 py-3 hover:bg-[#F8F9FA] flex items-center gap-2 transition-colors"
                        >
                            <Upload size={18} /> Import Excel
                        </button>
                        <button
                            onClick={openCreate}
                            data-testid="add-student-button"
                            className="bg-[#FF3B30] text-white font-bold px-6 py-3 hover:bg-[#D70015] flex items-center gap-2 transition-colors"
                        >
                            <Plus size={18} /> Add Student
                        </button>
                    </div>
                </div>

                <div className="flex gap-3 mb-6 flex-wrap">
                    {[
                        { k: "all", label: "All" },
                        { k: "paid", label: "Paid" },
                        { k: "due", label: "Due" },
                    ].map((f) => (
                        <button
                            key={f.k}
                            onClick={() => setFilter(f.k)}
                            data-testid={`filter-${f.k}`}
                            className={`px-4 py-2 text-sm font-bold border transition-colors ${
                                filter === f.k
                                    ? "bg-[#111111] text-white border-[#111111]"
                                    : "bg-white text-[#111111] border-[#E5E5E5] hover:border-[#111111]"
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                    <input
                        type="text"
                        placeholder="Search by name, roll, department…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        data-testid="student-search"
                        className="flex-1 min-w-[220px] bg-[#F8F9FA] border border-[#E5E5E5] px-4 py-2 text-sm focus:border-[#111111] outline-none"
                    />
                </div>

                <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-[#F8F9FA] border-b-2 border-[#111111]">
                            <tr>
                                <th className="text-left font-bold py-3 px-4">Name</th>
                                <th className="text-left font-bold py-3 px-4">Roll No.</th>
                                <th className="text-left font-bold py-3 px-4">Department</th>
                                <th className="text-left font-bold py-3 px-4">Bus</th>
                                <th className="text-left font-bold py-3 px-4">Payment</th>
                                <th className="text-right font-bold py-3 px-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody data-testid="students-table">
                            {filtered.map((s) => (
                                <tr key={s.id} className="border-b border-[#E5E5E5] hover:bg-[#F8F9FA]">
                                    <td className="py-3 px-4 font-medium">{s.name}</td>
                                    <td className="py-3 px-4 font-mono text-xs">{s.roll_number}</td>
                                    <td className="py-3 px-4 text-[#666666]">{s.department}</td>
                                    <td className="py-3 px-4 text-[#666666]">
                                        {busById(s.assigned_bus_id)?.bus_name || "—"}
                                    </td>
                                    <td className="py-3 px-4">
                                        <button
                                            onClick={() => togglePayment(s)}
                                            data-testid={`toggle-payment-${s.roll_number}`}
                                            className={`font-bold px-2 py-1 text-xs uppercase tracking-wider border transition-colors ${
                                                s.payment_status === "paid"
                                                    ? "bg-[#34C759]/10 text-[#34C759] border-[#34C759]/30 hover:bg-[#34C759]/20"
                                                    : "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/30 hover:bg-[#FF3B30]/20"
                                            }`}
                                        >
                                            {s.payment_status}
                                        </button>
                                    </td>
                                    <td className="py-3 px-4">
                                        <div className="flex gap-1 justify-end">
                                            <button
                                                onClick={() => setQrOpen(s)}
                                                data-testid={`qr-btn-${s.roll_number}`}
                                                title={s.qr_token ? "View QR" : "Not assigned"}
                                                className={`p-2 transition-colors ${
                                                    s.qr_token
                                                        ? "hover:bg-[#111111] hover:text-white"
                                                        : "text-[#666666]/50 hover:bg-[#666666]/10"
                                                }`}
                                            >
                                                <QrCode size={16} />
                                            </button>
                                            <button
                                                onClick={() => openEdit(s)}
                                                data-testid={`edit-btn-${s.roll_number}`}
                                                title="Edit"
                                                className="p-2 hover:bg-[#111111] hover:text-white transition-colors"
                                            >
                                                <Pencil size={16} />
                                            </button>
                                            <button
                                                onClick={() => remove(s)}
                                                data-testid={`delete-btn-${s.roll_number}`}
                                                title="Delete"
                                                className="p-2 hover:bg-[#FF3B30] hover:text-white transition-colors"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr>
                                    <td colSpan="6" className="p-8 text-center text-[#666666]">
                                        No students match.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Student Form Modal — landscape (wide, capped-height) so it never
                overflows the viewport; only the field area scrolls, header/footer stay put. */}
            {formOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white border-2 border-[#111111] w-full max-w-3xl max-h-[90vh] flex flex-col animate-slide-up">
                        <div className="flex justify-between items-center px-6 py-4 border-b-2 border-[#111111] shrink-0">
                            <h3 className="font-display text-2xl">{editing ? "Edit" : "Add"} Student</h3>
                            <button
                                onClick={() => setFormOpen(false)}
                                data-testid="close-form"
                                className="p-1 hover:bg-[#F8F9FA]"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={save} className="flex flex-col flex-1 overflow-hidden">
                            <div className="p-6 space-y-4 overflow-y-auto flex-1">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {[
                                        { k: "name", label: "Full Name", req: true },
                                        { k: "roll_number", label: "Roll Number", req: true },
                                        { k: "department", label: "Department", req: true },
                                        { k: "phone", label: "Phone" },
                                        { k: "email", label: "Email", type: "email" },
                                    ].map((f) => (
                                        <div key={f.k}>
                                            <label className="label-caps block mb-2">{f.label}</label>
                                            <input
                                                data-testid={`form-${f.k}`}
                                                required={f.req}
                                                type={f.type || "text"}
                                                value={form[f.k]}
                                                onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                                                className="w-full bg-[#F8F9FA] border border-[#E5E5E5] px-4 py-2 focus:border-[#111111] outline-none"
                                            />
                                        </div>
                                    ))}
                                    <div>
                                        <label className="label-caps block mb-2">Assigned Bus</label>
                                        <select
                                            data-testid="form-bus"
                                            value={form.assigned_bus_id || ""}
                                            onChange={(e) => setForm({ ...form, assigned_bus_id: e.target.value })}
                                            className="w-full bg-[#F8F9FA] border border-[#E5E5E5] px-4 py-2 focus:border-[#111111] outline-none"
                                        >
                                            <option value="">— None —</option>
                                            {buses.map((b) => (
                                                <option key={b.id} value={b.id}>
                                                    {b.bus_name} · {b.route_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="label-caps block mb-2">QR Code</label>
                                        {form.qr_code ? (
                                            <div className="flex gap-2">
                                                <div
                                                    data-testid="assigned-qr-code"
                                                    className="flex-1 bg-[#F8F9FA] border border-[#E5E5E5] px-4 py-2 font-mono text-sm flex items-center truncate"
                                                >
                                                    {form.qr_code}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => setScannerOpen(true)}
                                                    data-testid="rescan-qr-button"
                                                    title="Scan a different card"
                                                    className="px-3 border border-[#111111] font-bold text-xs uppercase tracking-wider hover:bg-[#111111] hover:text-white transition-colors whitespace-nowrap"
                                                >
                                                    Rescan
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={clearQr}
                                                    data-testid="unassign-qr-button"
                                                    title="Unassign"
                                                    className="px-3 border border-[#E5E5E5] text-[#666666] hover:border-[#111111] hover:text-[#111111] transition-colors"
                                                >
                                                    <X size={16} />
                                                </button>
                                                {editing?.qr_token && (
                                                    <button
                                                        type="button"
                                                        onClick={reportLost}
                                                        data-testid="report-lost-button"
                                                        title="Report this card lost"
                                                        className="px-3 border border-[#FF3B30] text-[#FF3B30] font-bold text-xs uppercase tracking-wider hover:bg-[#FF3B30] hover:text-white transition-colors whitespace-nowrap"
                                                    >
                                                        Lost
                                                    </button>
                                                )}
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => setScannerOpen(true)}
                                                data-testid="scan-qr-button"
                                                className="w-full border border-dashed border-[#666666]/40 text-[#666666] py-2 flex items-center justify-center gap-2 hover:border-[#111111] hover:text-[#111111] transition-colors"
                                            >
                                                <ScanLine size={18} /> Scan QR Code
                                            </button>
                                        )}
                                        <p className="text-xs text-[#666666] mt-1">
                                            Scan the code printed on the student's card — codes are pre-approved,
                                            not typed in manually.
                                        </p>
                                    </div>
                                    <div>
                                        <label className="label-caps block mb-2">Payment Status</label>
                                        <div className="grid grid-cols-2 gap-2">
                                            {["paid", "due"].map((p) => (
                                                <button
                                                    type="button"
                                                    key={p}
                                                    onClick={() => setForm({ ...form, payment_status: p })}
                                                    data-testid={`form-payment-${p}`}
                                                    className={`py-2 font-bold uppercase tracking-wider text-sm border transition-colors ${
                                                        form.payment_status === p
                                                            ? p === "paid"
                                                                ? "bg-[#34C759] text-white border-[#34C759]"
                                                                : "bg-[#FF3B30] text-white border-[#FF3B30]"
                                                            : "bg-white text-[#111111] border-[#E5E5E5]"
                                                    }`}
                                                >
                                                    {p}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-3 px-6 py-4 border-t border-[#E5E5E5] shrink-0">
                                <button
                                    type="button"
                                    onClick={() => setFormOpen(false)}
                                    className="flex-1 border border-[#111111] font-bold py-3 hover:bg-[#F8F9FA]"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    data-testid="form-submit"
                                    className="flex-1 bg-[#FF3B30] text-white font-bold py-3 hover:bg-[#D70015]"
                                >
                                    {editing ? "Save Changes" : "Add Student"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {qrOpen && <QRModal student={qrOpen} onClose={() => setQrOpen(null)} />}

            {scannerOpen && (
                <QRScannerModal
                    title="Scan Student's QR Card"
                    onDetected={handleScanned}
                    onClose={() => setScannerOpen(false)}
                />
            )}
        </AdminLayout>
    );
}

function QRModal({ student, onClose }) {
    const [qrData, setQrData] = useState(null);
    const [notAssigned, setNotAssigned] = useState(false);
    useEffect(() => {
        api
            .get(`/students/${student.id}/qr`)
            .then((r) => setQrData(r.data))
            .catch(() => setNotAssigned(true));
    }, [student.id]);

    const download = () => {
        if (!qrData) return;
        const a = document.createElement("a");
        a.href = qrData.qr_image;
        a.download = `scanix-${student.roll_number}.png`;
        a.click();
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-white border-2 border-[#111111] w-full max-w-md animate-slide-up">
                <div className="flex justify-between items-center px-6 py-4 border-b-2 border-[#111111]">
                    <h3 className="font-display text-2xl">Student QR</h3>
                    <button onClick={onClose} data-testid="close-qr" className="p-1 hover:bg-[#F8F9FA]">
                        <X size={20} />
                    </button>
                </div>
                <div className="p-8 text-center">
                    <div className="font-display text-2xl">{student.name}</div>
                    <div className="font-mono text-sm text-[#666666] mt-1 mb-6">{student.roll_number}</div>
                    {notAssigned ? (
                        <div className="w-64 h-64 mx-auto bg-[#F8F9FA] border border-dashed border-[#E5E5E5] flex flex-col items-center justify-center gap-2 text-[#666666]">
                            <QrCode size={32} className="opacity-40" />
                            <p className="text-sm px-6">No QR code assigned yet — allot one from the Edit form.</p>
                        </div>
                    ) : qrData ? (
                        <img
                            src={qrData.qr_image}
                            alt="QR"
                            data-testid="student-qr-image"
                            className="mx-auto w-64 h-64 border-2 border-[#111111]"
                        />
                    ) : (
                        <div className="w-64 h-64 mx-auto bg-[#F8F9FA]" />
                    )}
                    <p className="text-xs font-mono text-[#666666] mt-4">{qrData?.qr_token}</p>
                    <button
                        onClick={download}
                        disabled={!qrData}
                        data-testid="download-qr"
                        className="mt-6 bg-[#FF3B30] text-white font-bold px-6 py-3 hover:bg-[#D70015] inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <Download size={16} /> Download QR PNG
                    </button>
                </div>
            </div>
        </div>
    );
}
